import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Text,
  VStack,
  SimpleGrid,
  HStack,
  Badge,
  Divider,
  Table,
  TableCaption,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Spinner,
  Image,
  IconButton,
  ModalFooter,
  Select,
  Textarea,
  Input,
  Tooltip,
  Checkbox,
} from '@chakra-ui/react'
import { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Pencil, X } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { resolveSupportTypeLabel, useSupportTypeOptions } from '@/hooks/useSupportTypeOptions'
import type { RequestOption } from '@/types/ticket'
import type { FieldEngineerRecommendation } from '@/types/ticket'
import type { FieldEngineerSummary } from '@/store/parentTicket'
import type { FESchedule } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { formatWallClockDateTime } from '@/lib/utils/dateTimeUtils'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import PartsInfoSection from './PartsInfoSection'
import OverviewSectionCard from './overview/OverviewSectionCard'
import StoreMapCard from './overview/StoreMapCard'
import LocationBreadcrumbChips from './overview/LocationBreadcrumbChips'
import PropertyRow from './overview/PropertyRow'
import FEDispatchFlow from '@/components/forms/parentTicket/FEDispatchFlow'
import { ScheduleLocationFields, ScheduleMapPreview } from '@/components/forms/parentTicket/feDispatchUtils'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { ParentTicketAttachmentsList } from '@/components/helpdesk/TicketDetail/ParentTicketAttachmentsList'
import type { ParentTicketAttachment } from '@/types/ticket'
import TaskBadge from '@/components/helpdesk/TicketDetail/tasks/TaskBadge'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import {
  countTasksBySourceField,
  formatSourceFieldCounter,
  type TaskSourceField,
} from '@/components/helpdesk/TicketDetail/tasks/taskSourceFieldUtils'

type Asset = {
  id: number
  tag_number?: string | null
  model?: string | null
  serial_number?: string | null
  brand_name?: string | null
  category_name?: string | null
  function?: string | null
  [key: string]: any
}

type TicketData = {
  ticketNumber?: string | null
  description?: string | null
  notes?: string | null
  attachments?: ParentTicketAttachment[]
  scheduledAt?: string | null
  requestedAt?: string | null
  ticketSubmittedAt?: string | null
  ageingLabel?: string | null
  holdTimeLabel?: string | null
  isAgePaused?: boolean
  assets?: Asset[]
  subject?: string | null
  type?: string | null
  source?: string | null
  priority?: string | null
  severity?: string | null
  supportType?: string | null
  status?: string | null
  agentName?: string | null
  clientReferenceNumber?: string | null
  purchaseOrder?: import('@/types/ticket').ClientPurchaseOrder | null
  hasPartsNeeded?: boolean
  certificationRequired?: boolean
  certification_required?: boolean
  brandId?: number | null
  brand_id?: number | null
  brandName?: string | null
  brand_name?: string | null
  ticketParts?: import('@/types/parentTicket').TicketPart[]
}

type Props = {
  /** Full request object from the API (ticket_requests table) */
  request?: RequestOption | null
  /** Parent ticket data (parent_tickets table) */
  ticket?: TicketData | null
  /** Ticket ID for loading FE schedules and parts (when in View page) */
  ticketId?: string | null
  /** Store ID for Recommend FE (View page) */
  storeId?: number | null
  /** Store/Client code for Recommend FE API (View page) - use client code e.g. JFC, not store branch code */
  storeCode?: string | null
  /** Client code for Recommend FE API (X-Client-Code) - from ticket request; use this for recommend, not storeCode */
  clientCode?: string | null
  /** Callback after FE schedules change (refresh activity log) */
  onSchedulesChange?: () => void | Promise<void>
  /** Callback after parts change (refresh activity log) */
  onPartsChange?: () => void
  /** Open a procurement task in the Tasks tab drawer */
  onOpenProcurementTask?: (taskId: number) => void
  /** Linked child tasks for field-level badges (view/detail) */
  ticketTasks?: ChildTicketTask[]
  onCreateFieldTask?: (sourceField: TaskSourceField) => void
  onOpenFieldTask?: (task: ChildTicketTask) => void
  /** Callback after notes are updated (refresh ticket in View) */
  onNotesUpdate?: () => void
  /** True while ticket is refetching after FE schedule add/edit/delete (show preloader in FE Schedule block) */
  feScheduleRefreshing?: boolean
  /** Primary engineer (same as Assigned Team card) */
  primaryEngineer?: FieldEngineerSummary | null
  /** First additional FE, kept for backward compatibility. */
  backupEngineer?: FieldEngineerSummary | null
  /** Ordered additional FEs. */
  backupEngineers?: FieldEngineerSummary[] | null
  /** Store details (shown inside Ticket Information) */
  storeDetails?: {
    locationType?: 'store' | 'location' | null
    businessUnitName?: string | null
    storeName?: string | null
    branchCode?: string | null
    contactPerson?: string | null
    email?: string | null
    contactNumber?: string | null
    storeAddress?: string | null
    latitude?: number | null
    longitude?: number | null
    municipality?: string | null
    province?: string | null
    country?: string | null
  } | null
  /** Geographic location (identity_db) — resolved labels for display */
  locationDetails?: {
    region?: string | null
    areaCluster?: string | null
    province?: string | null
    town?: string | null
  } | null

  isReadOnly?: boolean
  /** Omit outer card chrome; tighter grids (e.g. parent ticket view single card) */
  embedded?: boolean
  /** Which blocks to render (tabbed ticket view) */
  contentMode?: 'all' | 'overview' | 'details' | 'assets'
  /** When set, render only the matching operational section (task drawer embed) */
  taskSection?: 'fe' | 'parts' | 'permit'
  /** Link new/updated FE schedules to a child task (workflow step) */
  childTicketId?: number | null
  /** FE calendar assignments (for overview scheduled date) */
  feSchedules?: FESchedule[]
  onWorkPermitUpload?: (ticketId: string, file: File) => Promise<void>
}

function Pair({ label, value }: { label: string; value?: string | null }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color={colors.text} fontWeight="500">
        {value || '—'}
      </Text>
    </Box>
  )
}

function LocationFields({
  locationDetails,
  spacing,
}: {
  locationDetails: NonNullable<Props['locationDetails']>
  spacing: number
}) {
  return (
    <VStack align="stretch" spacing={spacing}>
      <Pair label="Region" value={locationDetails.region} />
      <Pair label="Area Cluster" value={locationDetails.areaCluster} />
      <Pair label="Province" value={locationDetails.province} />
      <Pair label="Town" value={locationDetails.town} />
    </VStack>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatDateUTC(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function SectionHeader({ children, mb }: { children: React.ReactNode; mb?: number }) {
  const colors = useThemeColors()
  return (
    <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wider" mb={mb}>
      {children}
    </Text>
  )
}

export default function TicketProperties({
  request,
  ticket,
  ticketId,
  storeId,
  storeCode,
  clientCode,
  onSchedulesChange,
  onPartsChange,
  onOpenProcurementTask,
  ticketTasks = [],
  onCreateFieldTask,
  onOpenFieldTask,
  onNotesUpdate,
  feScheduleRefreshing,
  primaryEngineer,
  backupEngineer,
  backupEngineers,
  storeDetails,
  locationDetails,
  isReadOnly,
  embedded = false,
  contentMode = 'all',
  taskSection,
  childTicketId,
  feSchedules = [],
}: Props) {
  const { labelMap: severityLabels } = useTicketOptions('severity')
  const { options: feRoleOptions, labelMap: feRoleLabels } = useTicketOptions('fe_role')
  const embedMode = Boolean(taskSection)
  const showAssetsOnly = contentMode === 'assets'
  const showOverview = !embedMode && !showAssetsOnly && (contentMode === 'all' || contentMode === 'overview')
  const showDetails = !embedMode && !showAssetsOnly && (contentMode === 'all' || contentMode === 'details')
  const showEmbeddedFe = taskSection === 'fe'
  const showEmbeddedParts = taskSection === 'parts'
  const useOverviewCards = contentMode === 'overview'
  const hasLocationDetails = Boolean(
    locationDetails &&
      [locationDetails.region, locationDetails.areaCluster, locationDetails.province, locationDetails.town].some(
        (value) => Boolean(value?.trim()),
      ),
  )
  const recommendClientCode = clientCode?.trim() || ''
  /** FE recommender needs a store in the ticket's SIMA client DB + X-Client-Code (not branch store code). */
  const canRecommendFE = storeId != null && recommendClientCode.length > 0
  const recommendFEDisabledReason =
    storeId == null
      ? showEmbeddedFe
        ? 'Select a store in the task panel above before recommending field engineers.'
        : 'Link a store on the FE Plotting task to use FE recommendation.'
      : recommendClientCode.length === 0
        ? 'Client code is missing — FE recommendation needs the ticket request client (e.g. DXC - JFC OISS), not only a branch code.'
        : ''
  const procurementTaskCount = useMemo(
    () => countTasksBySourceField(ticketTasks, 'tools_parts'),
    [ticketTasks],
  )
  const documentTaskCount = useMemo(
    () => countTasksBySourceField(ticketTasks, 'attachments'),
    [ticketTasks],
  )
  const showFieldTaskBadges = Boolean(ticketId && onCreateFieldTask && onOpenFieldTask)
  const colors = useThemeColors()
  const { labelByCode: supportTypeLabels } = useSupportTypeOptions()
  const gridX = embedded ? 6 : 8
  const gridY = embedded ? 2 : 3
  const sectionVSpacing = embedded ? 3 : 4
  const mainVSpacing = useOverviewCards ? 4 : embedded ? 3 : 5
  const { showSuccess, showError } = useToastNotification()
  const { isOpen: isRecommendOpen, onOpen: onRecommendOpen, onClose: onRecommendClose } = useDisclosure()
  const [recommendations, setRecommendations] = useState<FieldEngineerRecommendation[]>([])
  const [recommendLoading, setRecommendLoading] = useState(false)
  /** Selected date for daily timeline (default today) */
  const [recommendSelectedDate, setRecommendSelectedDate] = useState<Date>(() => new Date())
  /** Per-FE schedules for the selected date (cross-ticket), keyed by fe_user_id */
  const [feAvailabilityByUser, setFeAvailabilityByUser] = useState<Record<number, FESchedule[]>>({})
  const [recommendScheduleLoading, setRecommendScheduleLoading] = useState(false)
  const { isOpen: isDeleteScheduleOpen, onOpen: onDeleteScheduleOpen, onClose: onDeleteScheduleClose } = useDisclosure()
  const [scheduleToDelete, setScheduleToDelete] = useState<number | null>(null)
  const cancelDeleteRef = useRef<HTMLButtonElement>(null)
  const { isOpen: isAssignModalOpen, onOpen: onAssignModalOpen, onClose: onAssignModalClose } = useDisclosure()
  const [assignModalRec, setAssignModalRec] = useState<FieldEngineerRecommendation | null>(null)
  const [assignModalDateStr, setAssignModalDateStr] = useState('')
  const [assignModalTime, setAssignModalTime] = useState('09:00')
  const [assignModalEndTime, setAssignModalEndTime] = useState('10:00')
  const [assignModalRole, setAssignModalRole] = useState<'primary' | 'additional'>('primary')
  const [assignSubmitting, setAssignSubmitting] = useState(false)
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null)
  const [editScheduleTime, setEditScheduleTime] = useState('09:00')
  const [editScheduleEndTime, setEditScheduleEndTime] = useState('10:00')
  const [editScheduleRole, setEditScheduleRole] = useState<'primary' | 'additional'>('primary')
  const [editScheduleEndDate, setEditScheduleEndDate] = useState('')
  const [editLatitude, setEditLatitude] = useState('')
  const [editLongitude, setEditLongitude] = useState('')
  const [editLocationAddress, setEditLocationAddress] = useState('')
  const [editContactOverride, setEditContactOverride] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [assignModalEndDateStr, setAssignModalEndDateStr] = useState('')
  const [assignModalLatitude, setAssignModalLatitude] = useState('')
  const [assignModalLongitude, setAssignModalLongitude] = useState('')
  const [assignModalLocationAddress, setAssignModalLocationAddress] = useState('')
  const [assignModalContactOverride, setAssignModalContactOverride] = useState('')
  const [assignModalSpansMultipleDays, setAssignModalSpansMultipleDays] = useState(false)
  const [editSpansMultipleDays, setEditSpansMultipleDays] = useState(false)
  const [deleteSubmittingId, setDeleteSubmittingId] = useState<number | null>(null)
  const [schedulesRefreshKey, setSchedulesRefreshKey] = useState(0)
  const { isOpen: isNotesModalOpen, onOpen: onNotesModalOpen, onClose: onNotesModalClose } = useDisclosure()
  const [notesDraft, setNotesDraft] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)
  const [engineerPatchLoading, setEngineerPatchLoading] = useState<'primary' | 'additional' | null>(null)

  const refreshSchedulesInView = useCallback(() => {
    setSchedulesRefreshKey((k) => k + 1)
    onSchedulesChange?.()
  }, [onSchedulesChange])

  /** Selected date as Y-m-d for API */
  const recommendDateStr = useMemo(
    () =>
      `${recommendSelectedDate.getFullYear()}-${String(recommendSelectedDate.getMonth() + 1).padStart(2, '0')}-${String(recommendSelectedDate.getDate()).padStart(2, '0')}`,
    [recommendSelectedDate]
  )

  const fetchFEAvailabilityForUsers = useCallback(
    async (dateStr: string, feUserIds: number[], replaceAll = true) => {
      if (!ticketId) return
      const uniqueIds = [...new Set(feUserIds.filter((id) => Number.isFinite(id) && id > 0))]
      if (uniqueIds.length === 0) {
        if (replaceAll) setFeAvailabilityByUser({})
        return
      }

      if (replaceAll) setRecommendScheduleLoading(true)
      try {
        const res =
          storeId != null
            ? await TicketService.getFEAvailabilityGlobal(
                Number(storeId),
                uniqueIds,
                dateStr,
                recommendClientCode || undefined,
                ticketId,
              )
            : await TicketService.getFEAvailabilityGlobalForLocation(
                uniqueIds,
                dateStr,
                recommendClientCode || undefined,
                ticketId,
              )

        const next: Record<number, FESchedule[]> = {}
        for (const id of uniqueIds) {
          next[id] = res.success && res.data ? (res.data[id] ?? []) : []
        }

        if (replaceAll) {
          setFeAvailabilityByUser(next)
        } else {
          setFeAvailabilityByUser((prev) => ({ ...prev, ...next }))
        }
      } finally {
        if (replaceAll) setRecommendScheduleLoading(false)
      }
    },
    [ticketId, storeId, recommendClientCode],
  )

  const fetchFEAvailabilityForDate = useCallback(
    async (dateStr: string) => {
      if (!ticketId || recommendations.length === 0) return
      const feUserIds = recommendations
        .map((rec) => rec.user_id ?? rec.field_engineer_id)
        .filter((id): id is number => id != null && Number.isFinite(id) && id > 0)
      await fetchFEAvailabilityForUsers(dateStr, feUserIds, true)
    },
    [ticketId, recommendations, fetchFEAvailabilityForUsers],
  )

  useEffect(() => {
    if (isRecommendOpen && ticketId && recommendations.length > 0) {
      fetchFEAvailabilityForDate(recommendDateStr)
    }
  }, [isRecommendOpen, ticketId, recommendations, recommendDateStr, fetchFEAvailabilityForDate])

  const handleRecommendFE = useCallback(async () => {
    if (!canRecommendFE) return
    setRecommendLoading(true)
    setRecommendations([])
    onRecommendOpen()
    const certRequired = Boolean(
      ticket?.certificationRequired ?? ticket?.certification_required ?? false,
    )
    const brandName =
      (ticket?.brandName ?? ticket?.brand_name)?.trim() || null
    const requiredCertifications =
      certRequired && brandName ? [brandName] : undefined
    const res = await TicketService.recommendFieldEngineers(Number(storeId), recommendClientCode, {
      ...(requiredCertifications ? { requiredCertifications } : {}),
    })
    setRecommendLoading(false)
    if (res.success && res.data) setRecommendations(res.data)
    else if (!res.success && res.message) showError(res.message)
  }, [canRecommendFE, storeId, recommendClientCode, onRecommendOpen, showError, ticket])

  /** Hour slots for daily timeline (24-hour, 00:00–23:00) */
  const timelineHours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
  const today = new Date()
  const isSelectedToday =
    recommendSelectedDate.getFullYear() === today.getFullYear() &&
    recommendSelectedDate.getMonth() === today.getMonth() &&
    recommendSelectedDate.getDate() === today.getDate()

  const getFeUserId = (rec: FieldEngineerRecommendation) => rec.user_id ?? rec.field_engineer_id

  /** Parse "H:i" to minutes since midnight; returns 0 for invalid/empty */
  const timeToMinutes = useCallback((time: string | null | undefined): number => {
    if (!time || typeof time !== 'string') return 0
    const parts = time.trim().slice(0, 5).split(':')
    const h = parseInt(parts[0] ?? '0', 10)
    const m = parseInt(parts[1] ?? '0', 10)
    return h * 60 + m
  }, [])

  /** Schedules for an FE that cover the given hour (range [hour:00, hour+1:00) intersects schedule range) */
  const getScheduleForHour = useCallback(
    (feUserId: number, hour: number): FESchedule[] => {
      const list = feAvailabilityByUser[feUserId] ?? []
      const hourStart = hour * 60
      const hourEnd = hourStart + 60
      return list.filter((s) => {
        const startM = timeToMinutes(s.schedule_time)
        const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
        return startM < hourEnd && endM > hourStart
      })
    },
    [feAvailabilityByUser, timeToMinutes]
  )

  /** Merged timeline blocks for an FE on the selected date: each block spans [startHour, endHour) with one schedule or available. */
  const getTimelineBlocks = useCallback(
    (feUserId: number): { startHour: number; endHour: number; schedule: FESchedule | null }[] => {
      const list = (feAvailabilityByUser[feUserId] ?? []).filter((s) =>
        s.schedule_date?.startsWith(recommendDateStr)
      )
      const hourToSchedule: (FESchedule | null)[] = Array(24).fill(null)
      for (const s of list) {
        const startM = timeToMinutes(s.schedule_time)
        const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
        const startHour = Math.max(0, Math.floor(startM / 60))
        // End hour inclusive: 09:00–12:00 spans columns 9,10,11,12 (4 hrs)
        const endHour = Math.min(24, Math.floor(endM / 60) + 1)
        for (let h = startHour; h < endHour; h++) hourToSchedule[h] = s
      }
      const blocks: { startHour: number; endHour: number; schedule: FESchedule | null }[] = []
      let i = 0
      while (i < 24) {
        const s = hourToSchedule[i]
        let end = i + 1
        while (end < 24 && hourToSchedule[end] === s) end++
        blocks.push({ startHour: i, endHour: end, schedule: s })
        i = end
      }
      return blocks
    },
    [feAvailabilityByUser, recommendDateStr, timeToMinutes]
  )

  /** Add one hour to "HH:mm" (e.g. "09:00" -> "10:00", "23:00" -> "00:00" wraps) */
  const addOneHour = useCallback((timeStr: string): string => {
    const m = timeToMinutes(timeStr) + 60
    const h = Math.floor(m / 60) % 24
    const min = m % 60
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }, [timeToMinutes])

  const openAssignModal = useCallback(
    (rec: FieldEngineerRecommendation, dateStr: string, timeStr = '09:00') => {
      setAssignModalRec(rec)
      setAssignModalDateStr(dateStr)
      setAssignModalEndDateStr(dateStr)
      setAssignModalTime(timeStr)
      setAssignModalEndTime(addOneHour(timeStr))
      setAssignModalRole('primary')
      setAssignModalSpansMultipleDays(false)
      setAssignModalContactOverride(request?.contactNumber ?? storeDetails?.contactNumber ?? '')
      setAssignModalLocationAddress(storeDetails?.storeAddress ?? '')
      setAssignModalLatitude(
        storeDetails?.latitude != null && Number.isFinite(storeDetails.latitude)
          ? String(storeDetails.latitude)
          : '',
      )
      setAssignModalLongitude(
        storeDetails?.longitude != null && Number.isFinite(storeDetails.longitude)
          ? String(storeDetails.longitude)
          : '',
      )
      setEditingScheduleId(null)
      onAssignModalOpen()
    },
    [onAssignModalOpen, addOneHour, request?.contactNumber, storeDetails?.contactNumber, storeDetails?.storeAddress, storeDetails?.latitude, storeDetails?.longitude]
  )

  const existingSchedulesForModal = useMemo(() => {
    if (!assignModalRec || !assignModalDateStr) return []
    const feUserId = getFeUserId(assignModalRec)
    const list = feAvailabilityByUser[feUserId] ?? []
    return list.filter((s) => s.schedule_date?.startsWith(assignModalDateStr))
  }, [assignModalRec, assignModalDateStr, feAvailabilityByUser])

  const handleEditSchedule = useCallback((s: FESchedule) => {
    setEditingScheduleId(s.id)
    const start = s.schedule_time ?? '09:00'
    setEditScheduleTime(start)
    setEditScheduleEndTime(s.schedule_end_time ?? addOneHour(start))
    setEditScheduleRole(s.role === 'primary' ? 'primary' : 'additional')
    setEditScheduleEndDate(s.schedule_end_date ?? s.schedule_date?.slice(0, 10) ?? '')
    const scheduleDate = s.schedule_date?.slice(0, 10) ?? ''
    const endDate = s.schedule_end_date?.slice(0, 10) ?? scheduleDate
    setEditSpansMultipleDays(Boolean(endDate && scheduleDate && endDate !== scheduleDate))
    setEditLatitude(
      s.latitude != null
        ? String(s.latitude)
        : storeDetails?.latitude != null && Number.isFinite(storeDetails.latitude)
          ? String(storeDetails.latitude)
          : '',
    )
    setEditLongitude(
      s.longitude != null
        ? String(s.longitude)
        : storeDetails?.longitude != null && Number.isFinite(storeDetails.longitude)
          ? String(storeDetails.longitude)
          : '',
    )
    setEditLocationAddress(s.location_address ?? storeDetails?.storeAddress ?? '')
    setEditContactOverride(s.contact_number_override ?? request?.contactNumber ?? storeDetails?.contactNumber ?? '')
  }, [addOneHour, request?.contactNumber, storeDetails?.contactNumber, storeDetails?.storeAddress, storeDetails?.latitude, storeDetails?.longitude])

  const handleSaveEditSchedule = useCallback(async () => {
    if (!ticketId || editingScheduleId == null) return
    if (timeToMinutes(editScheduleEndTime) <= timeToMinutes(editScheduleTime)) {
      showError(new Error('End time must be after start time.'))
      return
    }
    setEditSubmitting(true)
    try {
      const res = await TicketService.updateFESchedule(ticketId, editingScheduleId, {
        schedule_time: editScheduleTime,
        schedule_end_time: editScheduleEndTime,
        schedule_end_date: editSpansMultipleDays ? (editScheduleEndDate || null) : null,
        role: editScheduleRole,
        latitude: editLatitude ? Number(editLatitude) : null,
        longitude: editLongitude ? Number(editLongitude) : null,
        location_address: editLocationAddress || null,
        contact_number_override: editContactOverride || null,
        ...(childTicketId != null ? { child_ticket_id: childTicketId } : {}),
      })
      if (res.success) {
        showSuccess('Schedule updated')
        setEditingScheduleId(null)
        fetchFEAvailabilityForDate(recommendDateStr)
        refreshSchedulesInView()
      } else {
        const msg = res.message ?? 'Update failed'
        showError(
          new Error(
            /conflict|overlap|already has an assignment/i.test(msg)
              ? 'Schedule conflict detected. The Field Engineer already has an assignment during this time.'
              : msg
          )
        )
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setEditSubmitting(false)
    }
  }, [ticketId, editingScheduleId, editScheduleTime, editScheduleEndTime, editScheduleEndDate, editSpansMultipleDays, editScheduleRole, editLatitude, editLongitude, editLocationAddress, editContactOverride, childTicketId, timeToMinutes, recommendDateStr, fetchFEAvailabilityForDate, refreshSchedulesInView, showSuccess, showError])

  const handleDeleteScheduleClick = useCallback((scheduleId: number) => {
    setScheduleToDelete(scheduleId)
    onDeleteScheduleOpen()
  }, [onDeleteScheduleOpen])

  const handleDeleteScheduleConfirm = useCallback(async () => {
    if (!ticketId || scheduleToDelete == null) return
    const scheduleId = scheduleToDelete
    setScheduleToDelete(null)
    onDeleteScheduleClose()
    setDeleteSubmittingId(scheduleId)
    try {
      const res = await TicketService.deleteFESchedule(ticketId, scheduleId)
      if (res.success) {
        showSuccess('Schedule removed')
        if (editingScheduleId === scheduleId) setEditingScheduleId(null)
        fetchFEAvailabilityForDate(recommendDateStr)
        refreshSchedulesInView()
      } else {
        showError(new Error(res.message ?? 'Delete failed'))
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setDeleteSubmittingId(null)
    }
  }, [ticketId, scheduleToDelete, editingScheduleId, recommendDateStr, fetchFEAvailabilityForDate, refreshSchedulesInView, onDeleteScheduleClose, showSuccess, showError])

  const handleAssignSubmit = useCallback(async () => {
    if (!ticketId || !assignModalRec) return
    const feUserId = getFeUserId(assignModalRec)
    setAssignSubmitting(true)
    try {
      if (timeToMinutes(assignModalEndTime) <= timeToMinutes(assignModalTime)) {
        showError(new Error('End time must be after start time.'))
        setAssignSubmitting(false)
        return
      }
      const res = await TicketService.createFESchedule(ticketId, {
        fe_user_id: feUserId,
        schedule_date: assignModalDateStr,
        schedule_end_date: assignModalSpansMultipleDays ? (assignModalEndDateStr || null) : null,
        schedule_time: assignModalTime,
        schedule_end_time: assignModalEndTime,
        role: assignModalRole,
        latitude: assignModalLatitude ? Number(assignModalLatitude) : null,
        longitude: assignModalLongitude ? Number(assignModalLongitude) : null,
        location_address: assignModalLocationAddress || null,
        contact_number_override: assignModalContactOverride || null,
        ...(childTicketId != null ? { child_ticket_id: childTicketId } : {}),
      })
      if (res.success) {
        showSuccess('FE scheduled')
        onAssignModalClose()
        setAssignModalRec(null)
        void fetchFEAvailabilityForUsers(recommendDateStr, [feUserId], false)
        refreshSchedulesInView()
      } else {
        const msg = res.message ?? ''
        if (/conflict|overlap|already has an assignment/i.test(msg)) {
          showError(new Error('Schedule conflict detected. The Field Engineer already has an assignment during this time.'))
        } else {
          showError(new Error(res.message ?? 'Assign failed'))
        }
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setAssignSubmitting(false)
    }
  }, [ticketId, assignModalRec, assignModalDateStr, assignModalEndDateStr, assignModalSpansMultipleDays, assignModalTime, assignModalEndTime, assignModalRole, assignModalLatitude, assignModalLongitude, assignModalLocationAddress, assignModalContactOverride, childTicketId, timeToMinutes, recommendDateStr, fetchFEAvailabilityForUsers, refreshSchedulesInView, onAssignModalClose, showSuccess, showError])

  const handleClearEngineer = useCallback(
    async (role: 'primary' | 'additional') => {
      if (!ticketId || isReadOnly) return
      setEngineerPatchLoading(role)
      try {
        const res = await TicketService.patchParentTicket(ticketId, {
          ...(role === 'primary'
            ? { primaryEngineerId: null }
            : { additionalFeUserIds: [], additionalFeUserId: null, backupEngineerId: null }),
        })
        if (res.success) {
          showSuccess(role === 'primary' ? 'Primary engineer removed' : 'Additional FE removed')
          await refreshSchedulesInView()
        } else {
          showError(res.message ?? 'Failed to remove engineer')
        }
      } catch (err) {
        showError(err as Error)
      } finally {
        setEngineerPatchLoading(null)
      }
    },
    [ticketId, isReadOnly, refreshSchedulesInView, showSuccess, showError]
  )

  if (!request && !ticket) {
    return null
  }

  const serviceLabel = request
    ? [request.serviceParent, request.serviceType].filter(Boolean).join(' → ')
    : undefined

  const assets = ticket?.assets ?? []
  const showAssets = assets.length > 0
  const ticketParts = Array.isArray(ticket?.ticketParts) ? ticket.ticketParts : []
  const ticketAttachments = ticket?.attachments ?? []
  const showPartsCard =
    useOverviewCards &&
    Boolean(ticketId) &&
    (Boolean(ticket?.hasPartsNeeded) || ticketParts.length > 0)
  const showAttachmentsCard = useOverviewCards && ticketAttachments.length > 0

  if (showAssetsOnly) {
    if (!showAssets) return null
    return (
      <OverviewSectionCard
        title="Assets"
        rightAction={
          <Text fontSize="xs" color={colors.textSecondary}>
            {assets.length} linked
          </Text>
        }
      >
        <VStack align="stretch" spacing={0}>
          {assets.map((a, idx) => (
            <Box
              key={a.id ?? `asset-${idx}`}
              pt={idx === 0 ? 0 : 1.5}
              mt={idx === 0 ? 0 : 1.5}
              borderTop={idx === 0 ? undefined : '1px solid'}
              borderColor={colors.borderLight}
            >
              <HStack justify="space-between" align="flex-start" spacing={3}>
                <Box minW={0} flex="1">
                  <Text fontSize="sm" color={colors.text}>
                    {a.tag_number || '—'}
                    {a.serial_number ? (
                      <Text as="span" fontSize="xs" color={colors.textMuted}>
                        {' · SN'}
                        {a.serial_number}
                      </Text>
                    ) : null}
                  </Text>
                </Box>
                <Text fontSize="sm" color={colors.textSecondary} textAlign="right">
                  {a.model || a.category_name || '—'}
                </Text>
              </HStack>
            </Box>
          ))}
        </VStack>
      </OverviewSectionCard>
    )
  }

  return (
      <Box
        minW={0}
        {...(embedded
          ? {}
          : {
              bg: colors.cardBg,
              borderRadius: 'lg',
              border: '1px solid',
              borderColor: colors.border,
              overflow: 'hidden',
            })}
      >
        {/* ─── Header ─── */}
        {!embedded && (
          <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
            <Text fontWeight="700" color={colors.primary}>
              Ticket Details
            </Text>
          </Box>
        )}

        <Box px={embedded ? 0 : 4} py={embedded ? 0 : 4}>
          <VStack align="stretch" spacing={mainVSpacing}>

            {/* ════════════════════════════════════════════
                SECTION 1: Request Details
                (from ticket_requests table)
               ════════════════════════════════════════════ */}
            {showOverview && request && !useOverviewCards && (() => {
              const requestBody = (
                <>
                  <SimpleGrid key="req-contact" columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Contact Name" value={request.contactName} />
                    <Pair label="Contact Email" value={request.contactEmail} />
                    <Pair label="Contact Number" value={request.contactNumber} />
                  </SimpleGrid>

                  <SimpleGrid key="req-service" columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Service Type" value={serviceLabel || undefined} />
                    <Pair label="Request Source" value={request.requestSource} />
                    <Box>
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
                        Categories
                      </Text>
                      {request.categories && request.categories.length > 0 ? (
                        <HStack spacing={1} flexWrap="wrap">
                          {request.categories.map((cat, idx) => (
                            <Badge
                              key={`${cat}-${idx}`}
                              fontSize="xs"
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cat}
                            </Badge>
                          ))}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color={colors.text} fontWeight="500">—</Text>
                      )}
                    </Box>
                  </SimpleGrid>

                  <SimpleGrid key="req-schedule" columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Requested Quantity" value={request.requestedQuantity?.toString()} />
                    <Pair label="Preferred Schedule" value={formatWallClockDateTime(request.preferredScheduleAt)} />
                    <Pair label="Preferred Contact At" value={formatWallClockDateTime(request.preferredContactAt)} />
                  </SimpleGrid>

                  <SimpleGrid key="req-dates" columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Submitted" value={formatDate(request.submittedAt)} />
                    <Pair label="Acknowledged" value={formatDate(request.acknowledgedAt)} />
                    <Pair label="Requested At" value={formatDate(request.requestedAt)} />
                  </SimpleGrid>

                  {/* Request Description (only if different from ticket description) */}
                  {request.description && request.description !== ticket?.description && (
                    <Box key="req-description">
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                        Request Description
                      </Text>
                      <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7">
                        {request.description}
                      </Text>
                    </Box>
                  )}
                </>
              )
              return (
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Request Details</SectionHeader>
                    {request.id && (
                      <Badge fontSize="xs" bg="purple.50" color="purple.700" border="1px solid" borderColor="purple.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                        {request.id}
                      </Badge>
                    )}
                  </HStack>
                  <VStack align="stretch" spacing={sectionVSpacing}>{requestBody}</VStack>
                </Box>
              )
            })()}

            {/* ════════════════════════════════════════════
                SECTION 2: Parent Ticket Details
                (from parent_tickets table)
               ════════════════════════════════════════════ */}
            {showOverview && ticket && useOverviewCards && (
                <OverviewSectionCard key="overview-ticket-details" title="Ticket details">
                  <VStack align="stretch" spacing={2.5}>
                    {ticket.subject?.trim() && ticket.subject.trim() !== ticket.description?.trim() ? (
                      <Text fontSize="sm" fontWeight="500" color={colors.text} lineHeight="1.5">
                        {ticket.subject}
                      </Text>
                    ) : null}
                    <Text fontSize="sm" color={colors.textSecondary} whiteSpace="pre-wrap" lineHeight="1.7">
                      {ticket.description?.trim() ? ticket.description : '—'}
                    </Text>
                    <Divider borderColor={colors.borderLight} />
                    {ticket.clientReferenceNumber?.trim() ? (
                      <PropertyRow label="Client reference" value={ticket.clientReferenceNumber} />
                    ) : null}
                    <PropertyRow label="Received Date" value={formatDate(ticket.requestedAt)} />
                    <PropertyRow label="Created At" value={formatDate(ticket.ticketSubmittedAt)} />
                    <PropertyRow label="Ageing" value={ticket.ageingLabel?.trim() ? ticket.ageingLabel : '—'} />
                    <PropertyRow
                      label="Hold Time"
                      value={
                        ticket.isAgePaused && ticket.holdTimeLabel?.trim()
                          ? ticket.holdTimeLabel
                          : '—'
                      }
                    />
                    <PropertyRow
                      label="Notes"
                      valueAlign="left"
                      value={
                        <HStack justify="flex-start" spacing={1} role="group" w="100%">
                          <Text as="span" fontSize="xs" textAlign="left" whiteSpace="pre-wrap" noOfLines={3} flex="1">
                            {ticket.notes?.trim() ? ticket.notes : '—'}
                          </Text>
                          {ticketId && onNotesUpdate ? (
                            <IconButton
                              aria-label="Edit notes"
                              size="xs"
                              variant="ghost"
                              colorScheme="gray"
                              icon={<Pencil size={12} />}
                              flexShrink={0}
                              onClick={() => {
                                setNotesDraft(ticket?.notes ?? '')
                                onNotesModalOpen()
                              }}
                            />
                          ) : null}
                        </HStack>
                      }
                    />
                  </VStack>
                </OverviewSectionCard>
            )}
            {showOverview && ticket && useOverviewCards && hasLocationDetails && locationDetails && (
                  <OverviewSectionCard key="overview-location" title="Location details">
                    <LocationBreadcrumbChips locationDetails={locationDetails} />
                  </OverviewSectionCard>
            )}
            {showOverview && ticket && useOverviewCards && storeDetails && (
                  <OverviewSectionCard key="overview-store" title={storeDetails.locationType === 'location' ? 'Location' : 'Store'}>
                    <StoreMapCard storeDetails={storeDetails} />
                  </OverviewSectionCard>
            )}
            {showOverview && ticket && showPartsCard && ticketId && (
              <OverviewSectionCard
                key="overview-tools-parts"
                title="Tools / Parts"
                rightAction={
                  showFieldTaskBadges ? (
                    <TaskBadge
                      sourceField="tools_parts"
                      tasks={ticketTasks}
                      onCreate={onCreateFieldTask!}
                      onOpenTask={onOpenFieldTask!}
                    />
                  ) : undefined
                }
              >
                {formatSourceFieldCounter('tools_parts', procurementTaskCount) ? (
                  <Text fontSize="xs" color={colors.textSecondary} mb={2}>
                    {formatSourceFieldCounter('tools_parts', procurementTaskCount)}
                  </Text>
                ) : null}
                <PartsInfoSection
                  ticketId={ticketId}
                  isReadOnly={isReadOnly ?? false}
                  onPartsChange={onPartsChange}
                  onOpenProcurementTask={onOpenProcurementTask}
                  hideTitle
                  compactTable
                />
              </OverviewSectionCard>
            )}
            {showOverview && ticket && showAttachmentsCard && (
              <OverviewSectionCard
                key="overview-attachments"
                title="Attachments"
                rightAction={
                  <HStack spacing={2}>
                    {showFieldTaskBadges ? (
                      <TaskBadge
                        sourceField="attachments"
                        tasks={ticketTasks}
                        onCreate={onCreateFieldTask!}
                        onOpenTask={onOpenFieldTask!}
                      />
                    ) : null}
                    <Badge fontSize="xs" colorScheme="purple" variant="subtle" borderRadius="full" px={2}>
                      {ticketAttachments.length}
                    </Badge>
                  </HStack>
                }
              >
                {formatSourceFieldCounter('attachments', documentTaskCount) ? (
                  <Text fontSize="xs" color={colors.textSecondary} mb={2}>
                    {formatSourceFieldCounter('attachments', documentTaskCount)}
                  </Text>
                ) : null}
                <ParentTicketAttachmentsList
                  ticketId={ticketId}
                  attachments={ticket.attachments}
                  hideTitle
                  emptyMessage="No attachments on this ticket."
                />
              </OverviewSectionCard>
            )}

            {showOverview && ticket && !useOverviewCards && (
              <>
                {request && <Divider key="ticket-info-divider" borderColor={colors.borderLight} borderWidth="2px" />}
                <Box key="ticket-info-section">
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Ticket Information</SectionHeader>
                    {ticket.ticketNumber && (
                      <Badge fontSize="xs" bg="blue.50" color="blue.700" border="1px solid" borderColor="blue.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                        {ticket.ticketNumber}
                      </Badge>
                    )}
                  </HStack>
                  {contentMode === 'all' && (
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY} mb={4}>
                      <Pair label="Severity" value={ticket.severity != null ? (severityLabels[String(ticket.severity)] ?? ticket.severity ?? ticket.priority) : (ticket.priority ?? undefined)} />
                      <Pair label="Support Tier" value={resolveSupportTypeLabel(ticket.supportType, supportTypeLabels)} />
                      <Pair label="Source" value={ticket.source} />
                      <Pair
                        label="Purchase Order"
                        value={
                          ticket.purchaseOrder?.po_number
                            ? [
                                ticket.purchaseOrder.po_number,
                                ticket.purchaseOrder.description,
                              ]
                                .filter(Boolean)
                                .join(' — ')
                            : undefined
                        }
                      />
                    </SimpleGrid>
                  )}
                  <Box mt={4}>
                    <SectionHeader>Description</SectionHeader>
                    <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                      {ticket.description || '—'}
                    </Text>
                  </Box>
                  {storeDetails && (
                    <Box mt={5} pt={2}>
                      <SectionHeader mb={4}>{storeDetails.locationType === 'location' ? 'Location Details' : 'Store Details'}</SectionHeader>
                      <VStack align="stretch" spacing={sectionVSpacing}>
                        {storeDetails.locationType === 'location' ? (
                          <>
                            <Pair label="Location" value={storeDetails.storeName ?? storeDetails.storeAddress} />
                            {storeDetails.municipality ? (
                              <Pair label="Municipality" value={storeDetails.municipality} />
                            ) : null}
                            {storeDetails.province ? (
                              <Pair label="Province" value={storeDetails.province} />
                            ) : null}
                            {storeDetails.country ? (
                              <Pair label="Country" value={storeDetails.country} />
                            ) : null}
                          </>
                        ) : (
                          <>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                              <Pair label="Business Unit Name" value={storeDetails.businessUnitName} />
                              <Pair label="Store Name" value={storeDetails.storeName} />
                              <Pair label="Branch Code" value={storeDetails.branchCode} />
                            </SimpleGrid>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                              <Pair label="Contact Person" value={storeDetails.contactPerson} />
                              <Box>
                                <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>Email</Text>
                                {storeDetails.email ? (
                                  <Link href={`mailto:${storeDetails.email}`} color={colors.primary} fontWeight="600" _hover={{ textDecoration: 'underline' }} fontSize="sm">
                                    {storeDetails.email}
                                  </Link>
                                ) : (
                                  <Text fontSize="sm" color={colors.textMuted}>—</Text>
                                )}
                              </Box>
                              <Pair label="Contact Number" value={storeDetails.contactNumber} />
                            </SimpleGrid>
                          </>
                        )}
                        <Box>
                          <Pair label={storeDetails.locationType === 'location' ? 'Address' : 'Store Address'} value={storeDetails.storeAddress} />
                        </Box>
                      </VStack>
                    </Box>
                  )}
                  {hasLocationDetails && locationDetails && (
                    <Box mt={5} pt={2}>
                      <SectionHeader mb={4}>Location</SectionHeader>
                      <LocationFields locationDetails={locationDetails} spacing={sectionVSpacing} />
                    </Box>
                  )}
                  <Box mt={4}>
                    <ParentTicketAttachmentsList
                      ticketId={ticketId}
                      attachments={ticket.attachments}
                      title="Attachments"
                      emptyMessage="No attachments on this ticket."
                    />
                  </Box>
                </Box>
              </>
            )}

            {/* Parts information */}
            {(showDetails || showEmbeddedParts) && ticketId && (
              <>
                <Divider key="parts-divider" borderColor={colors.borderLight} />
                <Box key="parts-section">
                  <PartsInfoSection
                    ticketId={ticketId}
                    isReadOnly={isReadOnly ?? true}
                    onPartsChange={onPartsChange}
                    onOpenProcurementTask={onOpenProcurementTask}
                  />
                </Box>
              </>
            )}

            {/* FE Schedule */}
            {(showDetails || showEmbeddedFe) && ticketId && (
              <>
                {!showEmbeddedFe && <Divider key="fe-divider" borderColor={colors.borderLight} />}
                <Box key="fe-schedule-section">
                  {showEmbeddedFe ? (
                    <FEDispatchFlow
                      mode="immediate"
                      ticketId={ticketId}
                      clientCode={recommendClientCode}
                      storeId={storeId}
                      storeDetails={storeDetails ?? undefined}
                      requestContactNumber={request?.contactNumber}
                      ticketSubject={ticket?.subject}
                      primaryEngineer={primaryEngineer}
                      additionalEngineers={
                        backupEngineers?.length
                          ? backupEngineers
                          : backupEngineer
                            ? [backupEngineer]
                            : undefined
                      }
                      preferredScheduleAt={ticket?.scheduledAt ?? null}
                      certificationRequired={Boolean(
                        ticket?.certificationRequired ?? ticket?.certification_required ?? false,
                      )}
                      brandId={
                        ticket?.brandId != null
                          ? Number(ticket.brandId)
                          : ticket?.brand_id != null
                            ? Number(ticket.brand_id)
                            : null
                      }
                      feScheduleRefreshing={feScheduleRefreshing}
                      isReadOnly={isReadOnly}
                      onConfirm={() => {}}
                      onSchedulesChange={refreshSchedulesInView}
                      onClearEngineer={handleClearEngineer}
                      engineerPatchLoading={engineerPatchLoading}
                    />
                  ) : (
                    <>
                      <HStack justify="space-between" align="center" mb={2}>
                        <SectionHeader>FE Schedule</SectionHeader>
                        <HStack spacing={2}>
                          {ticketId && onOpenFieldTask ? (
                            <TaskBadge
                              sourceField="fe_visit"
                              tasks={ticketTasks}
                              onOpenTask={onOpenFieldTask}
                              allowCreate={false}
                            />
                          ) : null}
                          <Tooltip
                          label={canRecommendFE ? `Recommend field engineers for store (client: ${recommendClientCode})` : recommendFEDisabledReason}
                          isDisabled={canRecommendFE}
                          shouldWrapChildren
                        >
                          <Button
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={handleRecommendFE}
                            isLoading={recommendLoading}
                            isDisabled={!canRecommendFE}
                          >
                            Recommend FE
                          </Button>
                        </Tooltip>
                        </HStack>
                      </HStack>
                      {feScheduleRefreshing ? (
                        <HStack py={6} justify="center" gap={3}>
                          <Spinner size="sm" color={colors.primary} />
                          <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
                        </HStack>
                      ) : (
                        <>
                          {ticket?.scheduledAt && (
                            <Text fontSize="sm" color={colors.textSecondary} mb={2}>
                              Primary engineer schedule: {formatDateUTC(ticket.scheduledAt)}
                            </Text>
                          )}
                          {(primaryEngineer || backupEngineer || backupEngineers?.length) ? (
                            <VStack align="stretch" spacing={3}>
                              {primaryEngineer && (
                                <Box>
                                  <HStack justify="space-between" mb={1}>
                                    <Text fontSize="xs" color={colors.textSecondary} fontWeight="600">
                                      Primary Engineer
                                    </Text>
                                    {!isReadOnly && (
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        leftIcon={<X size={12} />}
                                        isLoading={engineerPatchLoading === 'primary'}
                                        onClick={() => void handleClearEngineer('primary')}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </HStack>
                                  <Box p={3} borderRadius="md" border="1px solid" borderColor={colors.borderLight} bg={colors.bg}>
                                    <Text fontSize="sm" fontWeight="600" color={colors.text}>
                                      {primaryEngineer.full_name ?? '—'}
                                    </Text>
                                    {primaryEngineer.user_email && (
                                      <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>{primaryEngineer.user_email}</Text>
                                    )}
                                    {(primaryEngineer.cluster || primaryEngineer.launch_point) && (
                                      <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
                                        {primaryEngineer.cluster ?? ''}{primaryEngineer.cluster && primaryEngineer.launch_point ? ' · ' : ''}{primaryEngineer.launch_point ?? ''}
                                      </Text>
                                    )}
                                  </Box>
                                </Box>
                              )}
                              {(backupEngineers?.length ? backupEngineers : backupEngineer ? [backupEngineer] : []).map((engineer, index) => (
                                <Box key={engineer.user_id ?? engineer.id ?? index}>
                                  <HStack justify="space-between" mb={1}>
                                    <Text fontSize="xs" color={colors.textSecondary} fontWeight="600">
                                      Additional FE #{index + 1}
                                    </Text>
                                    {!isReadOnly && index === 0 && (
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        leftIcon={<X size={12} />}
                                        isLoading={engineerPatchLoading === 'additional'}
                                        onClick={() => void handleClearEngineer('additional')}
                                      >
                                        Remove all
                                      </Button>
                                    )}
                                  </HStack>
                                  <Box p={3} borderRadius="md" border="1px solid" borderColor={colors.borderLight} bg={colors.bg}>
                                    <Text fontSize="sm" fontWeight="600" color={colors.text}>
                                      {engineer.full_name ?? '—'}
                                    </Text>
                                    {engineer.user_email && (
                                      <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>{engineer.user_email}</Text>
                                    )}
                                    {(engineer.cluster || engineer.launch_point) && (
                                      <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
                                        {engineer.cluster ?? ''}{engineer.cluster && engineer.launch_point ? ' · ' : ''}{engineer.launch_point ?? ''}
                                      </Text>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color={colors.textSecondary}>No FE assignments yet. Use Recommend FE to assign.</Text>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Notes + work permit (legacy / all mode only — overview uses Ticket details card) */}
            {showOverview && ticket != null && !useOverviewCards && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box role="group" position="relative">
                  <HStack justify="space-between" align="flex-start" gap={2}>
                    <SectionHeader>Notes</SectionHeader>
                    {ticketId && onNotesUpdate && (
                      <>
                        <IconButton
                          aria-label="Edit notes"
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          icon={<Pencil size={14} />}
                          opacity={{ base: 1, md: 0 }}
                          _groupHover={{ opacity: 1 }}
                          onClick={() => {
                            setNotesDraft(ticket?.notes ?? '')
                            onNotesModalOpen()
                          }}
                        />
                        <Button
                          size="xs"
                          variant="outline"
                          display={{ base: 'inline-flex', md: 'none' }}
                          onClick={() => {
                            setNotesDraft(ticket?.notes ?? '')
                            onNotesModalOpen()
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                  </HStack>
                  <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                    {ticket?.notes?.trim() ? ticket.notes : 'No notes'}
                  </Text>
                </Box>
              </>
            )}

            {showOverview && ticket != null && ticketId && onNotesUpdate && (
                <Modal isOpen={isNotesModalOpen} onClose={onNotesModalClose} size="lg">
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>Edit notes</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Add or edit notes..."
                        minH="120px"
                        resize="vertical"
                      />
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="ghost" mr={3} onClick={onNotesModalClose} isDisabled={notesSaving}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={async () => {
                          if (!ticketId) return
                          setNotesSaving(true)
                          const res = await TicketService.updateParentTicketNotes(ticketId, notesDraft.trim() || null)
                          setNotesSaving(false)
                          if (res.success) {
                            await onNotesUpdate?.()
                            onNotesModalClose()
                            showSuccess(res.message ?? 'Notes updated')
                          } else {
                            showError(res.message ?? 'Failed to update notes')
                          }
                        }}
                        isLoading={notesSaving}
                      >
                        Save
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
            )}

            {/* Assets */}
            {showDetails && showAssets && (
              <>
                <Divider key="assets-divider" borderColor={colors.borderLight} />
                <Box key="assets-section">
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Assets</SectionHeader>
                    <Badge fontSize="xs" colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                      {assets.length}
                    </Badge>
                  </HStack>
                  <TableContainer
                    maxH="260px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor={colors.borderLight}
                    borderRadius="md"
                  >
                    <Table size="sm" variant="simple" className="table">
                      <Thead bg={colors.bg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th fontSize="xs" py={2}>Tag #</Th>
                          <Th fontSize="xs" py={2}>Brand</Th>
                          <Th fontSize="xs" py={2}>Category</Th>
                          <Th fontSize="xs" py={2}>Model</Th>
                          <Th fontSize="xs" py={2}>Serial #</Th>
                          <Th fontSize="xs" py={2}>Function</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assets.map((a, idx) => (
                          <Tr key={a.id ?? `asset-detail-${idx}`}>
                            <Td fontSize="xs" py={1.5}>{a.tag_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.brand_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.category_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.model || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.serial_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.function || '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </VStack>
        </Box>

        {/* Recommend FE Modal - Daily timeline (FE rows, hour columns) */}
        <Modal isOpen={isRecommendOpen} onClose={onRecommendClose} size="6xl">
          <ModalOverlay />
          <ModalContent maxW="90vw">
            <ModalHeader pr={12}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Text>{showEmbeddedFe ? '3. Pick a day and an open slot' : 'Recommended Field Engineers'}</Text>
                <HStack flexShrink={0} spacing={2} align="center">
                  <IconButton
                    aria-label="Previous day"
                    size="sm"
                    variant="outline"
                    icon={<ChevronLeft size={16} />}
                    onClick={() =>
                      setRecommendSelectedDate((d) => {
                        const next = new Date(d)
                        next.setDate(next.getDate() - 1)
                        return next
                      })
                    }
                  />
                  <Input
                    type="date"
                    size="sm"
                    value={recommendDateStr}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v) setRecommendSelectedDate(new Date(v))
                    }}
                    maxW="160px"
                  />
                  <IconButton
                    aria-label="Next day"
                    size="sm"
                    variant="outline"
                    icon={<ChevronRight size={16} />}
                    onClick={() =>
                      setRecommendSelectedDate((d) => {
                        const next = new Date(d)
                        next.setDate(next.getDate() + 1)
                        return next
                      })
                    }
                  />
                  <Button
                    size="sm"
                    variant={isSelectedToday ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setRecommendSelectedDate(new Date())}
                  >
                    Today
                  </Button>
                </HStack>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              {recommendLoading ? (
                <HStack justify="center" py={8}>
                  <Spinner size="lg" />
                </HStack>
              ) : recommendations.length === 0 ? (
                <Text color={colors.textSecondary}>No recommendations. Ensure the ticket has a store with location.</Text>
              ) : (
                <>
                  {recommendScheduleLoading ? (
                    <HStack py={12} justify="center" gap={3}>
                      <Spinner size="md" color={colors.primary} />
                      <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
                    </HStack>
                  ) : (
                    <TableContainer overflowX="auto" whiteSpace="nowrap">
                      <Table size="sm" className="table">
                        <Thead>
                          <Tr>
                            <Th
                              className="sticky-col-left"
                              minW="220px"
                              borderRight="1px solid"
                              borderColor={colors.borderLight}
                              bg={colors.bg}
                            >
                              FE Name / Info
                            </Th>
                            {timelineHours.map((hour) => (
                              <Th
                                key={hour}
                                textAlign="center"
                                px={1}
                                fontSize="xs"
                                minW="60px"
                                border="1px solid"
                                borderColor={colors.borderLight}
                              >
                                {String(hour).padStart(2, '0')}:00
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {recommendations.map((rec, idx) => {
                            const feUserId = getFeUserId(rec)
                            return (
                              <Tr key={rec.field_engineer_id ?? idx}>
                                <Td
                                  className="sticky-col-left"
                                  borderRight="1px solid"
                                  borderColor={colors.borderLight}
                                  py={2}
                                  minW="220px"
                                  bg={colors.cardBg}
                                >
                                  <VStack align="stretch" spacing={0.5}>
                                    <Text fontWeight="600" fontSize="sm">
                                      {rec.full_name}
                                    </Text>
                                    <Text fontSize="xs" color={colors.textSecondary}>
                                      Score: {rec.score ?? '—'}
                                      {rec.distance_km != null ? ` · ${rec.distance_km} km` : ''}
                                    </Text>
                                    <Text fontSize="xs" color={colors.textSecondary}>
                                      Workload: {rec.workload ?? '—'} · Cluster: {rec.cluster ?? '—'}
                                    </Text>
                                    {rec.launch_point && (
                                      <Text fontSize="xs" color={colors.textMuted}>
                                        Launch: {rec.launch_point}
                                      </Text>
                                    )}
                                    {rec.has_required_certifications && (
                                      <Badge size="sm" colorScheme="green" fontSize="xs" w="fit-content">
                                        ✓ Certs
                                      </Badge>
                                    )}
                                    {rec.match_reasons && rec.match_reasons.length > 0 && (
                                      <HStack flexWrap="wrap" spacing={1} mt={0.5}>
                                        {rec.match_reasons.slice(0, 3).map((r, reasonIdx) => (
                                          <Badge key={`${r}-${reasonIdx}`} size="sm" variant="subtle" colorScheme="blue" fontSize="2xs">
                                            {r}
                                          </Badge>
                                        ))}
                                      </HStack>
                                    )}
                                  </VStack>
                                </Td>
                                {getTimelineBlocks(feUserId).map((block, blockIdx) => {
                                  const span = block.endHour - block.startHour
                                  const timeStr = `${String(block.startHour).padStart(2, '0')}:00`
                                  const isAvailable = block.schedule === null
                                  const canEditSchedule = block.schedule?.is_current_ticket === true
                                  const handleCellClick = () => {
                                    if (isAvailable) {
                                      openAssignModal(rec, recommendDateStr, timeStr)
                                    } else if (canEditSchedule && block.schedule) {
                                      setAssignModalRec(rec)
                                      setAssignModalDateStr(recommendDateStr)
                                      handleEditSchedule(block.schedule)
                                      onAssignModalOpen()
                                    }
                                  }
                                  return (
                                    <Td
                                      key={`${block.startHour}-${block.endHour}-${blockIdx}`}
                                      colSpan={span}
                                      px={1}
                                      py={1}
                                      minW={span * 60}
                                      cursor={isAvailable || canEditSchedule ? 'pointer' : 'default'}
                                      onClick={handleCellClick}
                                      _hover={isAvailable || canEditSchedule ? { bg: colors.borderLight } : undefined}
                                      border="1px solid"
                                      borderColor={colors.borderLight}
                                      bg={block.schedule ? 'orange.50' : undefined}
                                      verticalAlign="top"
                                    >
                                      {isAvailable ? (
                                        <Text fontSize="xs" color={colors.textSecondary}>
                                          Available
                                        </Text>
                                      ) : block.schedule ? (
                                        <Box fontSize="xs">
                                          <Text fontWeight="600">
                                            {block.schedule.schedule_time ?? '—'}
                                            {block.schedule.schedule_end_time ? `–${block.schedule.schedule_end_time}` : ''}
                                            {block.schedule.role === 'primary' ? ' (P)' : ' (S)'}
                                          </Text>
                                          <Text noOfLines={2} title={block.schedule.ticket_subject ?? undefined}>
                                            {block.schedule.ticket_subject || '—'}
                                          </Text>
                                          {!block.schedule.is_current_ticket ? (
                                            <Text fontSize="2xs" color={colors.textMuted}>
                                              Other ticket
                                            </Text>
                                          ) : (
                                            <Text fontSize="2xs" color={colors.textMuted}>
                                              Click to edit
                                            </Text>
                                          )}
                                        </Box>
                                      ) : null}
                                    </Td>
                                  )
                                })}
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Assign FE – confirm dispatch modal */}
        <Modal isOpen={isAssignModalOpen} onClose={() => { onAssignModalClose(); setAssignModalRec(null); setEditingScheduleId(null) }} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingScheduleId != null ? 'Edit schedule' : '4. Confirm dispatch'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                {assignModalRec && editingScheduleId == null && (
                  <Box p={3} bg={colors.sectionBg ?? 'gray.50'} borderRadius="md" border="1px solid" borderColor={colors.borderLight}>
                    <Text fontSize="xs" fontWeight="700" color={colors.textMuted} mb={2}>DISPATCH SUMMARY</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary}>Field engineer</Text>
                        <Text fontSize="sm" fontWeight="600">{assignModalRec.full_name ?? '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary}>Date</Text>
                        <Text fontSize="sm" fontWeight="600">{assignModalDateStr || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary}>Time</Text>
                        <Text fontSize="sm" fontWeight="600">
                          {assignModalTime}{assignModalEndTime ? ` – ${assignModalEndTime}` : ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary}>Contact</Text>
                        <Text fontSize="sm">{assignModalContactOverride || '—'}</Text>
                      </Box>
                    </SimpleGrid>
                    {assignModalLocationAddress && (
                      <Box mt={2}>
                        <Text fontSize="xs" color={colors.textSecondary}>Location</Text>
                        <Text fontSize="sm">{assignModalLocationAddress}</Text>
                      </Box>
                    )}
                  </Box>
                )}

                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Ticket subject</Text>
                  <Text fontSize="sm" color={colors.text}>{ticket?.subject ?? '—'}</Text>
                </Box>

                {existingSchedulesForModal.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="600" mb={2}>Scheduled times for this date</Text>
                    <VStack align="stretch" spacing={2}>
                      {existingSchedulesForModal.map((s) => {
                        const canEdit = s.is_current_ticket
                        return (
                          <Box
                            key={s.id}
                            p={2}
                            border="1px solid"
                            borderColor={colors.borderLight}
                            borderRadius="md"
                            bg={colors.bg}
                            opacity={canEdit ? 1 : 0.85}
                          >
                            {editingScheduleId === s.id && canEdit ? (
                              <VStack align="stretch" spacing={2}>
                                <HStack align="flex-end" spacing={2}>
                                  <Box flex={1}>
                                    <Text fontSize="xs" fontWeight="600" mb={1}>Start</Text>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={editScheduleTime}
                                      onChange={(e) => setEditScheduleTime(e.target.value)}
                                      w="full"
                                    />
                                  </Box>
                                  <Box flex={1}>
                                    <Text fontSize="xs" fontWeight="600" mb={1}>End</Text>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={editScheduleEndTime}
                                      onChange={(e) => setEditScheduleEndTime(e.target.value)}
                                      w="full"
                                    />
                                  </Box>
                                </HStack>
                                <HStack>
                                  <Text fontSize="xs" fontWeight="600" minW="40px">Role</Text>
                                  <Select
                                    size="sm"
                                    value={editScheduleRole}
                                    onChange={(e) => setEditScheduleRole(e.target.value as 'primary' | 'additional')}
                                  >
                                    {feRoleOptions.map((o) => (
                                      <option key={o.code} value={o.code}>{o.label}</option>
                                    ))}
                                  </Select>
                                </HStack>
                                <ScheduleLocationFields
                                  endDate={editScheduleEndDate}
                                  latitude={editLatitude}
                                  longitude={editLongitude}
                                  locationAddress={editLocationAddress}
                                  contactOverride={editContactOverride}
                                  dispatchBy={s.dispatch_by}
                                  spansMultipleDays={editSpansMultipleDays}
                                  onEndDateChange={setEditScheduleEndDate}
                                  onLatitudeChange={setEditLatitude}
                                  onLongitudeChange={setEditLongitude}
                                  onLocationAddressChange={setEditLocationAddress}
                                  onContactOverrideChange={setEditContactOverride}
                                  onSpansMultipleDaysChange={setEditSpansMultipleDays}
                                />
                              </VStack>
                            ) : (
                              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                <Box>
                                  <Text fontWeight="600" fontSize="sm">
                                    {s.schedule_time ?? '—'}
                                    {s.schedule_end_time ? ` – ${s.schedule_end_time}` : ''}
                                  </Text>
                                  <Text fontSize="xs" color={colors.textSecondary}>
                                    {feRoleLabels[s.role] ?? s.role}
                                    {!canEdit && ' · Other ticket (read-only)'}
                                  </Text>
                                </Box>
                                {canEdit && (
                                  <HStack>
                                    <Button size="xs" variant="outline" onClick={() => handleEditSchedule(s)}>
                                      Edit
                                    </Button>
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => handleDeleteScheduleClick(s.id)}
                                      isLoading={deleteSubmittingId === s.id}
                                      isDisabled={deleteSubmittingId != null}
                                    >
                                      Delete
                                    </Button>
                                  </HStack>
                                )}
                              </HStack>
                            )}
                          </Box>
                        )
                      })}
                    </VStack>
                  </Box>
                )}

                {editingScheduleId == null && (
                  <Box pt={existingSchedulesForModal.length > 0 ? 2 : 0} borderTop={existingSchedulesForModal.length > 0 ? '1px solid' : undefined} borderColor={colors.borderLight}>
                    <Text fontSize="sm" fontWeight="600" mb={2}>{existingSchedulesForModal.length > 0 ? 'Add another time' : 'Time range'}</Text>
                    <VStack align="stretch" spacing={3}>
                      <HStack align="flex-end" spacing={3}>
                        <Box flex={1}>
                          <Text fontSize="xs" color={colors.textSecondary} mb={1}>Start</Text>
                          <Input
                            type="time"
                            value={assignModalTime}
                            onChange={(e) => setAssignModalTime(e.target.value)}
                          />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" color={colors.textSecondary} mb={1}>End</Text>
                          <Input
                            type="time"
                            value={assignModalEndTime}
                            onChange={(e) => setAssignModalEndTime(e.target.value)}
                          />
                        </Box>
                      </HStack>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary} mb={1}>Role</Text>
                        <Select
                          value={assignModalRole}
                          onChange={(e) => setAssignModalRole(e.target.value as 'primary' | 'additional')}
                        >
                          {feRoleOptions.map((o) => (
                            <option key={o.code} value={o.code}>{o.label}</option>
                          ))}
                        </Select>
                      </Box>
                      <ScheduleLocationFields
                        endDate={assignModalEndDateStr}
                        latitude={assignModalLatitude}
                        longitude={assignModalLongitude}
                        locationAddress={assignModalLocationAddress}
                        contactOverride={assignModalContactOverride}
                        spansMultipleDays={assignModalSpansMultipleDays}
                        readOnlyLocation={
                          Boolean(assignModalLatitude.trim() && assignModalLongitude.trim())
                        }
                        onEndDateChange={setAssignModalEndDateStr}
                        onLatitudeChange={setAssignModalLatitude}
                        onLongitudeChange={setAssignModalLongitude}
                        onLocationAddressChange={setAssignModalLocationAddress}
                        onContactOverrideChange={setAssignModalContactOverride}
                        onSpansMultipleDaysChange={setAssignModalSpansMultipleDays}
                      />
                    </VStack>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={2} onClick={() => { onAssignModalClose(); setAssignModalRec(null); setEditingScheduleId(null) }}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={editingScheduleId != null ? handleSaveEditSchedule : handleAssignSubmit}
                isLoading={editingScheduleId != null ? editSubmitting : assignSubmitting}
              >
                {editingScheduleId != null ? 'Save' : 'Confirm dispatch'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <AlertDialog isOpen={isDeleteScheduleOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteScheduleClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Remove scheduled time?</AlertDialogHeader>
              <AlertDialogBody>
                This will remove the Field Engineer from this time slot for the current ticket.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelDeleteRef} onClick={onDeleteScheduleClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleDeleteScheduleConfirm} ml={3}>
                  Remove
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    )
}
